import React, { JSX, useEffect, useState } from 'react';
import { Alert, Box, Button, Card, CardContent, CircularProgress, Typography } from '@mui/material';
import {
  CheckCircle as CheckCircleIcon,
  Email as EmailIcon,
  ErrorOutline as ErrorIcon,
  Refresh as RefreshIcon,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { useAuth } from 'hooks/AuthContext';
import { BackstoryPageProps } from './BackstoryTab';

export const EmailVerificationPage = (_props: BackstoryPageProps): JSX.Element => {
  const { verifyEmail, resendEmailVerification, getPendingVerificationEmail, isLoading, error } =
    useAuth();
  const navigate = useNavigate();
  const [status, setStatus] = useState<'pending' | 'success' | 'error'>('pending');
  const [message, setMessage] = useState('');
  const [userType, setUserType] = useState<string>('');

  useEffect(() => {
    const token = new URLSearchParams(window.location.search).get('token');
    const handleVerifyEmail = async (verificationToken: string): Promise<void> => {
      try {
        const result = await verifyEmail({ token: verificationToken });
        if (!result) {
          setStatus('error');
          setMessage('Email verification failed');
          return;
        }
        setStatus('success');
        setMessage(result.message);
        setUserType(result.userType);
        setTimeout(() => navigate('/login'), 3000);
      } catch {
        setStatus('error');
        setMessage('Email verification failed');
      }
    };

    if (token) {
      handleVerifyEmail(token);
    } else {
      setStatus('error');
      setMessage('Invalid verification link');
    }
  }, [navigate, verifyEmail]);

  const handleResendVerification = async (): Promise<void> => {
    const email = getPendingVerificationEmail();
    if (!email) {
      setMessage('No pending verification email found.');
      return;
    }
    try {
      if (await resendEmailVerification(email)) setMessage('Verification email sent successfully!');
    } catch {
      setMessage('Failed to resend verification email.');
    }
  };

  return (
    <Box
      sx={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        bgcolor: 'grey.50',
        p: 2,
      }}
    >
      <Card sx={{ maxWidth: 500, width: '100%' }}>
        <CardContent sx={{ p: 4 }}>
          <VerificationStatus status={status} userType={userType} />
          {isLoading && (
            <Box display="flex" justifyContent="center" my={3}>
              <CircularProgress />
            </Box>
          )}
          {(message || error) && (
            <Alert
              severity={status === 'success' ? 'success' : status === 'error' ? 'error' : 'info'}
              sx={{ mt: 2 }}
            >
              {message || error}
            </Alert>
          )}
          {status === 'success' && (
            <Box mt={3} textAlign="center">
              <Typography variant="body2" color="text.secondary" mb={2}>
                You will be redirected to the login page in a few seconds...
              </Typography>
              <Button variant="contained" onClick={() => navigate('/login')} fullWidth>
                Go to Login
              </Button>
            </Box>
          )}
          {status === 'error' && (
            <Box mt={3}>
              <Button
                variant="outlined"
                onClick={handleResendVerification}
                disabled={isLoading}
                startIcon={<RefreshIcon />}
                fullWidth
                sx={{ mb: 2 }}
              >
                Resend Verification Email
              </Button>
              <Button variant="contained" onClick={() => navigate('/login')} fullWidth>
                Back to Login
              </Button>
            </Box>
          )}
        </CardContent>
      </Card>
    </Box>
  );
};

function VerificationStatus(props: {
  status: 'pending' | 'success' | 'error';
  userType: string;
}): JSX.Element {
  if (props.status === 'success') {
    return (
      <Box textAlign="center" mb={3}>
        <CheckCircleIcon sx={{ fontSize: 64, color: 'success.main', mb: 2 }} />
        <Typography variant="h4" gutterBottom color="success.main">
          Email Verified!
        </Typography>
        <Typography color="text.secondary">
          Your {props.userType} account has been successfully activated.
        </Typography>
      </Box>
    );
  }
  if (props.status === 'error') {
    return (
      <Box textAlign="center" mb={3}>
        <ErrorIcon sx={{ fontSize: 64, color: 'error.main', mb: 2 }} />
        <Typography variant="h4" gutterBottom color="error.main">
          Verification Failed
        </Typography>
        <Typography color="text.secondary">We couldn&apos;t verify your email address.</Typography>
      </Box>
    );
  }
  return (
    <Box textAlign="center" mb={3}>
      <EmailIcon sx={{ fontSize: 64, color: 'primary.main', mb: 2 }} />
      <Typography variant="h4" gutterBottom>
        Verifying Email
      </Typography>
      <Typography color="text.secondary">
        Please wait while we verify your email address...
      </Typography>
    </Box>
  );
}
